import type { PropsWithChildren } from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useIsAppleOs } from '@frameio/vapor';
import { PanelLayoutContext } from '../../contexts';
import { getCommandList } from '../COMMAND_LIST';
import { CommandRegistry } from '../CommandRegistry';
import {
  CustomShortcutsContext,
  type CustomShortcutsContextValue,
} from '../context/CustomShortcutsContext';
import { useCustomShortcutsStore } from '../hooks/useCustomShortcutsStore';
import type { Command } from '../types/Command';

export const CommandRegistryContext = createContext<CommandRegistry | null>(
  null
);

const getDefaultCommands = (): Command[] =>
  getCommandList()
    .map((category) =>
      category.orderedCommands.map((command) => ({
        ...command,
        displayCategory: category.categoryName,
      }))
    )
    .flat(1);

type Props = PropsWithChildren<{
  commands?: Array<Command>;
}>;

/**
 * Provider for `<KeyboardShortcut />`. Responsibilities include:
 * - Own singleton instance of `CommandRegistry`.
 * - Register each command from getCommandList().
 * - Detect operating system in order to enable Command vs Ctrl key bindings.
 * - Manage user-customized shortcuts and surface them via `CustomShortcutsContext`.
 */
export function CommandRegistryContextProvider({
  children,
  commands: commandsProp,
}: Props) {
  const commands = commandsProp ?? getDefaultCommands();
  const isApple = useIsAppleOs();

  const primedPanelArea = useContextSelector(
    PanelLayoutContext,
    (value) => value?.primedPanelArea
  );

  const { customizedShortcuts, setShortcut, resetShortcut, resetMany } =
    useCustomShortcutsStore();

  const [value, setValue] = useState<CommandRegistry | null>(null);
  const commandsString = JSON.stringify(commands);
  useEffect(() => {
    const registry = new CommandRegistry({ isApple });
    commandsString.toString();

    commands.forEach((command, i) => {
      const customCommandKeys = customizedShortcuts[command.id];

      const cmd = {
        ...command,
        ...(customCommandKeys
          ? {
              keys: customCommandKeys,
              alternativeKeys: undefined,
              isCustomized: true,
            }
          : {}),
      };

      registry.register(cmd, i);
    });

    setValue(registry);

    return () => registry.unmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isApple,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(commands),
    customizedShortcuts,
  ]);

  useEffect(() => {
    value?.prime(primedPanelArea);
  }, [primedPanelArea, value]);

  const customShortcutsContextValue: CustomShortcutsContextValue = useMemo(
    () => ({ customizedShortcuts, setShortcut, resetShortcut, resetMany }),
    [customizedShortcuts, setShortcut, resetShortcut, resetMany]
  );

  return (
    <CommandRegistryContext.Provider value={value}>
      <CustomShortcutsContext.Provider value={customShortcutsContextValue}>
        {children}
      </CustomShortcutsContext.Provider>
    </CommandRegistryContext.Provider>
  );
}
